import React, { createContext, useEffect, useState } from "react";
import { keycloak, initKeycloak } from "./keycloak";
import AuthService from "./auth.services";
import AxiosService from "../../utils/AxiosService";
import Spinner from "../../components/Spinner";
import AuthError from "./AuthError";

export const KeycloakContext = createContext(undefined);

export function KeycloakProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(false);
  const [error, setError] = useState(null);

  const initKeyCloakProvider = async () => {
    keycloak.onAuthSuccess = () => setAuthenticated(true);
    keycloak.onAuthRefreshSuccess = () => {
      setAuthenticated(true);
      AxiosService.setAuthenticationToken(keycloak.token);
    };
    keycloak.onAuthLogout = () => setAuthenticated(false);
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => setAuthenticated(false));
    };

    let isAuth = false;
    try {
      isAuth = await initKeycloak({
        onLoad: "check-sso",
        pkceMethod: "S256",
        checkLoginIframe: false,
      });
    } catch (e) {
      console.error("Keycloak init failed:", e);
      setError("Sign-in failed. Please try again.");
      setInitialized(true);
      return;
    }
    setAuthenticated(isAuth);

    if (isAuth && keycloak.token) {
      AxiosService.setAuthenticationToken(keycloak.token);
      setAdmin(
        keycloak.tokenParsed?.resource_access?.reports?.roles?.includes(
          "reports-admin"
        ) ?? false
      );

      // Fetch user from backend
      try {
        const userResponse = await AuthService.fetchUser();
        setUser(userResponse.data);
      } catch (e) {
        console.error("Failed to load user from backend:", e);
        const status = e.response?.status;
        setError(
          status
            ? `The server rejected the request (HTTP ${status}).`
            : e.code === "ECONNABORTED"
            ? `The API at ${process.env.REACT_APP_HOME_URL} did not respond.`
            : `Could not reach the API at ${process.env.REACT_APP_HOME_URL}.`
        );
      }
    }
    setInitialized(true);
  };

  useEffect(() => {
    initKeyCloakProvider();
  }, []);

  const resolved = Spinner({ isFullScreen: true });

  return (
    <KeycloakContext.Provider
      value={{ keycloak, authenticated, initialized, user, admin }}
    >
      {error ? <AuthError message={error} /> : initialized ? children : resolved}
    </KeycloakContext.Provider>
  );
}
